/**
 * Chat API Client
 * 
 * Typed functions for all chat session and history endpoints.
 * Implements chat endpoints from backend/main.py.
 */

import { apiClient } from './client';
import type {
  ChatSession,
  ChatSessionsResponse,
  ChatHistoryMessage,
} from '../types/api';

/**
 * List all chat sessions
 * GET /chat/sessions
 */
export async function listChatSessions(limit = 50): Promise<ChatSessionsResponse> {
  const queryParams = new URLSearchParams();
  queryParams.append('limit', limit.toString());
  
  return apiClient.get<ChatSessionsResponse>(`/chat/sessions?${queryParams.toString()}`);
}

/**
 * Create a new chat session
 * POST /chat/sessions
 */
export async function createChatSession(): Promise<{ session: ChatSession }> {
  return apiClient.post<{ session: ChatSession }>('/chat/sessions', {});
}

/**
 * Get chat history for a session
 * GET /chat/history
 */
export async function getChatHistory(
  sessionId?: string,
  limit = 50
): Promise<{ messages: ChatHistoryMessage[]; session: ChatSession }> {
  const queryParams = new URLSearchParams();
  if (sessionId) queryParams.append('session_id', sessionId);
  queryParams.append('limit', limit.toString());
  
  return apiClient.get<{ messages: ChatHistoryMessage[]; session: ChatSession }>(
    `/chat/history?${queryParams.toString()}`
  );
}

/**
 * Clear all messages in a chat session
 * DELETE /chat/sessions/{session_id}/messages
 */
export async function clearChatMessages(sessionId: string): Promise<{ session: ChatSession }> {
  return apiClient.delete<{ session: ChatSession }>(`/chat/sessions/${sessionId}/messages`);
}

/**
 * Delete a chat session
 * DELETE /chat/sessions/{session_id}
 */
export async function deleteChatSession(sessionId: string): Promise<{
  deleted_session_id: string;
  replacement_session?: ChatSession;
}> {
  return apiClient.delete<{
    deleted_session_id: string;
    replacement_session?: ChatSession;
  }>(`/chat/sessions/${sessionId}`);
}
